import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getById, updateQuestion } from 'services/questions'; // Importa updateQuestion
import { getAll } from 'services/questionTypes';
import PropTypes from 'prop-types';

const EditQuestionModal = ({
  question_id,
  show,
  handleClose,
  refreshQuestions
}) => {
  const [questionData, setQuestionData] = useState({
    question_text: '',
    question_type_id: '',
    options: [] // Opciones de la pregunta
  });
  const [questionTypes, setQuestionTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOption, setNewOption] = useState(''); // Estado para la nueva opción

  useEffect(() => {
    if (question_id) {
      setLoading(true);
      // Cargar la información de la pregunta
      getById(question_id)
        .then(response => {
          console.log('Respuesta de la API:', response);
          if (response) {
            setQuestionData({
              question_text: response.question_text,
              question_type_id: response.question_type_id,
              options: response.question_options.map(option => ({
                id: option.id,
                option_text: option.option_text,
                order_index: option.order_index
              }))
            });
          } else {
            toast.error('Error al cargar los datos de la pregunta.', {
              theme: 'colored'
            });
          }
        })
        .catch(error => {
          console.error('Error al cargar los datos de la pregunta:', error);
          toast.error('Error al cargar los datos de la pregunta.', {
            theme: 'colored'
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [question_id]);

  useEffect(() => {
    // Cargar los tipos de preguntas
    getAll()
      .then(response => {
        if (response.result) {
          setQuestionTypes(response.questionTypes);
        } else {
          toast.error('Error al cargar los tipos de preguntas.', {
            theme: 'colored'
          });
        }
      })
      .catch(error => {
        console.error('Error al cargar los tipos de preguntas:', error);
        toast.error('Error al cargar los tipos de preguntas.', {
          theme: 'colored'
        });
      });
  }, []);

  const handleAddOption = () => {
    if (newOption.trim() === '') {
      toast.error('El texto de la opción no puede estar vacío.', {
        theme: 'colored'
      });
      return;
    }
    setQuestionData(prev => ({
      ...prev,
      options: [
        ...prev.options,
        {
          id: null, // ID nulo para nuevas opciones
          option_text: newOption,
          order_index: prev.options.length + 1 // Asignar el siguiente índice
        }
      ]
    }));
    setNewOption(''); // Limpiar el campo de nueva opción
  };

  const handleEditOption = (id, newText) => {
    setQuestionData(prev => ({
      ...prev,
      options: prev.options.map(option =>
        option.id === id ? { ...option, option_text: newText } : option
      )
    }));
  };

  const handleEditOrderIndex = (id, newIndex) => {
    setQuestionData(prev => ({
      ...prev,
      options: prev.options.map(option =>
        option.id === id
          ? { ...option, order_index: parseInt(newIndex, 10) || 0 }
          : option
      )
    }));
  };

  const handleDeleteOption = id => {
    setQuestionData(prev => ({
      ...prev,
      options: prev.options.filter(option => option.id !== id)
    }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    setLoading(true);

    // Preparar los datos para enviar al backend
    const payload = {
      question_text: questionData.question_text,
      question_type_id: questionData.question_type_id,
      options: questionData.options.map(option => ({
        id: option.id, // Incluye el ID de la opción si existe
        option_text: option.option_text,
        order_index: option.order_index
      }))
    };

    updateQuestion(question_id, payload) // Llama al método de actualización
      .then(response => {
        if (response.result) {
          toast.success('Pregunta actualizada exitosamente.', {
            theme: 'colored'
          });
          refreshQuestions(); // Recargar las preguntas
          handleClose(); // Cerrar el modal
        } else {
          toast.error('Error al actualizar la pregunta.', { theme: 'colored' });
        }
      })
      .catch(error => {
        console.error('Error al actualizar la pregunta:', error);
        toast.error('Error al actualizar la pregunta.', { theme: 'colored' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Editar Pregunta</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-3">Cargando datos...</div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row className="g-2">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Texto de la Pregunta</Form.Label>
                  <Form.Control
                    type="text"
                    name="question_text"
                    value={questionData.question_text}
                    onChange={e =>
                      setQuestionData({
                        ...questionData,
                        question_text: e.target.value
                      })
                    }
                    placeholder="Escribe la pregunta"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Tipo de Pregunta</Form.Label>
                  <Form.Select
                    name="question_type_id"
                    value={questionData.question_type_id}
                    onChange={e =>
                      setQuestionData({
                        ...questionData,
                        question_type_id: e.target.value
                      })
                    }
                    required
                  >
                    <option value="">Selecciona un tipo</option>
                    {questionTypes &&
                      questionTypes.length > 0 &&
                      questionTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Opciones</Form.Label>
                  <InputGroup className="mb-2">
                    <Form.Control
                      type="text"
                      placeholder="Escribe una opción"
                      value={newOption}
                      onChange={e => setNewOption(e.target.value)}
                    />
                    <Button variant="primary" onClick={handleAddOption}>
                      Agregar
                    </Button>
                  </InputGroup>
                  <ul className="list-group">
                    {questionData.options.map(option => (
                      <li
                        key={option.id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        <Form.Control
                          type="text"
                          value={option.option_text}
                          onChange={e =>
                            handleEditOption(option.id, e.target.value)
                          }
                          className="me-2"
                        />
                        <Form.Control
                          type="number"
                          value={option.order_index}
                          onChange={e =>
                            handleEditOrderIndex(option.id, e.target.value)
                          }
                          className="me-2"
                          style={{ width: '80px' }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteOption(option.id)}
                        >
                          Eliminar
                        </Button>
                      </li>
                    ))}
                  </ul>
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-3">
              <Button variant="secondary" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="ms-2"
                disabled={loading}
              >
                {loading ? 'Guardando...' : 'Actualizar Pregunta'}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

EditQuestionModal.propTypes = {
  question_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  show: PropTypes.bool,
  handleClose: PropTypes.func,
  refreshQuestions: PropTypes.func
};

export default EditQuestionModal;
